import React, { useState } from 'react';
import {
  List,
  ListItem,
  Paper,
  IconButton,
  Box,
  Typography,
  useTheme
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';

/**
 * DraggableList - A draggable list component that allows reordering items
 * 
 * @param {Array} items - Array of items to display
 * @param {Function} renderItem - Function to render each item (item, index, isDragging) => ReactNode
 * @param {Function} onReorder - Callback when items are reordered (newOrder) => void
 * @param {Object} sx - Additional styles for the list
 */
const DraggableList = ({ items, renderItem, onReorder, sx = {} }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const theme = useTheme();

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
    // Add drag handle styling
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (draggedIndex === null) return;
    
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      return;
    }

    // Create new order array
    const newOrder = [...items];
    const draggedItem = newOrder[draggedIndex];
    
    // Remove dragged item from original position
    newOrder.splice(draggedIndex, 1);
    
    // Insert dragged item at new position
    newOrder.splice(dropIndex, 0, draggedItem);
    
    // Update indices based on new order
    const updatedItems = newOrder.map((item, index) => ({
      ...item,
      orderIndex: index
    }));
    
    // Call the callback with the new order
    onReorder(updatedItems);
    
    // Reset drag state
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <Paper sx={{ ...sx }}>
      <List>
        {items.map((item, index) => {
          const isDragging = draggedIndex === index;
          const isDragOver = dragOverIndex === index;
          
          return (
            <ListItem
              key={item.id || index}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              sx={{
                cursor: 'move',
                opacity: isDragging ? 0.5 : 1,
                backgroundColor: isDragOver 
                  ? theme.palette.primary.light + '20' 
                  : 'transparent',
                borderLeft: isDragOver 
                  ? `3px solid ${theme.palette.primary.main}` 
                  : '3px solid transparent',
                mb: 1,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  transform: 'translateX(4px)'
                }
              }}
            >
              {/* Drag Handle */}
              <IconButton
                edge="start"
                sx={{ 
                  cursor: 'grab',
                  mr: 1,
                  '&:active': { cursor: 'grabbing' }
                }}
                disabled
                size="small"
              >
                <DragIndicatorIcon />
              </IconButton>
              
              {/* Render the custom item content */}
              <Box sx={{ width: '100%' }}>
                {renderItem(item, index, isDragging)}
              </Box>
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
};

export default DraggableList;

